import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Field, Label, makeStyles, shorthands, Switch, SwitchOnChangeData, Textarea } from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { Control, Controller, FieldErrors, UseFormRegister, UseFormSetValue, useWatch } from "react-hook-form";
import { GenerateVIFormInput } from "../../interfaces";

const useStyles = makeStyles({
  panel: {
    ...shorthands.padding("5px"),
    ...shorthands.border("1px", "solid", "#E0E0E0"),
    ...shorthands.borderRadius("6px"),
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "8px",
  },
  headerText: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  inputs: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    columnGap: "8px",
    rowGap: "8px",
  },
  fullWidth: {
    gridColumn: "1 / -1",
  },
});

interface SetExpiryPanelProps {
  register: UseFormRegister<GenerateVIFormInput>;
  errors: FieldErrors<GenerateVIFormInput>;
  setValue: UseFormSetValue<GenerateVIFormInput>;
  control: Control<GenerateVIFormInput>;
  isRequired?: boolean;
}

const DEFAULT_MESSAGE = "This document has expired.";

const SetExpiryPanel: React.FC<SetExpiryPanelProps> = ({ register, errors, setValue, control, isRequired = false }) => {
  const styles = useStyles();
  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);
  const expiryValue = useWatch({
    control,
    name: "expiry",
  });
  const expiryMessageValue = useWatch({
    control,
    name: "expiry_message",
  });
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const isPanelActive = isRequired || isExpanded;

  useEffect(() => {
    if ((expiryValue && expiryValue.length > 0) || (expiryMessageValue && expiryMessageValue.length > 0)) {
      setIsExpanded(true);
    }
  }, [expiryValue, expiryMessageValue]);

  useEffect(() => {
    if (isRequired) {
      setIsExpanded(true);
      if (!expiryMessageValue || expiryMessageValue.length === 0) {
        setValue("expiry_message", DEFAULT_MESSAGE, { shouldDirty: true });
      }
    }
  }, [expiryMessageValue, isRequired, setValue]);

  const handleToggle = (_event: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => {
    if (isRequired) return;
    setIsExpanded(Boolean(data.checked));
    if (!data.checked) {
      setValue("expiry", "");
      setValue("expiry_message", "");
    } else if (!expiryMessageValue || expiryMessageValue.length === 0) {
      setValue("expiry_message", DEFAULT_MESSAGE, { shouldDirty: true });
    }
  };

  const formatDate = useCallback((date?: Date | null) => {
    if (!date) return "";
    // Build an ISO calendar date without timezone conversion to avoid day shifts.
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <Label weight="semibold">Set Expiry</Label>
        </div>
        <Switch
          disabled={isRequired}
          checked={isPanelActive}
          onChange={handleToggle}
          label={isRequired ? "Required" : isPanelActive ? "On" : "Off"}
        />
      </div>

      {isPanelActive && (
        <>
          <div className={styles.inputs}>
            <Field validationMessage={errors.expiry?.message} label="Expiry date">
              <Controller
                name="expiry"
                control={control}
                rules={{
                  required: isPanelActive ? "Expiry date is required" : false,
                }}
                render={({ field: { value, onChange } }) => {
                  const selectedDate = value ? new Date(value) : undefined;
                  const sanitizedDate = selectedDate && !Number.isNaN(selectedDate.getTime()) ? selectedDate : undefined;

                  return (
                    <DatePicker
                      value={sanitizedDate}
                      placeholder="Select expiry date"
                      minDate={today}
                      allowTextInput={false}
                      formatDate={formatDate}
                      onSelectDate={(date) => {
                        onChange(formatDate(date));
                      }}
                    />
                  );
                }}
              />
            </Field>
            <Field
              className={styles.fullWidth}
              validationMessage={errors.expiry_message?.message}
              label="Message"
            >
              <Textarea
                rows={3}
                placeholder={DEFAULT_MESSAGE}
                {...register("expiry_message", {
                  required: isPanelActive ? "Expiry message is required" : false,
                  maxLength: {
                    value: 200,
                    message: "Message should be under 200 characters",
                  },
                })}
              />
            </Field>
          </div>
        </>
      )}
    </div>
  );
};

export default SetExpiryPanel;
